import { useState, type FormEvent } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { StatusBadge } from '../components/StatusBadge';
import { GuideLink } from '../evals/GuideLink';
import {
  countsText,
  kindText,
  notebookBack,
  notebookErrorText,
  noteDate,
  notesByArea,
  seenText,
  sourceText,
  statusText,
  type NotebookBasis,
  type NotebookNote,
  type NotebookView,
} from '../notebook';

// A boat's notebook (TOM-616, ADR 0370 §10): every note on the boat, in use
// or not, by part of the boat, with what each rests on. The notes hold what
// the boat's owners told Neo, so the admin states a reason first, as for a
// transcript; the server writes a fail-closed audit row before it answers
// (GET /v1/admin/boats/:boatId/notebook). The page never names an owner: a
// note says "an owner" or "a former owner". Reached from Boats and from a
// user's page, whose links hand over the boat's name and where "Back" goes.

/** What a note, or a dispute, rests on: the readings, the reads and the stretch of data. */
function Basis({ title, basis }: { title: string; basis: NotebookBasis }) {
  return (
    <div className="notebook-basis">
      <span className="notebook-basis-title">{title}</span>
      {basis.paths.length > 0 ? (
        <span>
          Based on{' '}
          {basis.paths.map((p, i) => (
            <span key={p}>
              {i > 0 ? ', ' : ''}
              <code>{p}</code>
            </span>
          ))}
        </span>
      ) : null}
      {basis.tools.length > 0 ? (
        <span>
          Read with{' '}
          {basis.tools.map((t, i) => (
            <span key={t}>
              {i > 0 ? ', ' : ''}
              <code>{t}</code>
            </span>
          ))}
        </span>
      ) : null}
      {basis.window !== null ? (
        <span>
          Data from {noteDate(basis.window.from)} to {noteDate(basis.window.to)}
        </span>
      ) : null}
    </div>
  );
}

function NoteCard({ note }: { note: NotebookNote }) {
  const status = statusText(note.status);
  const forgotten = note.status === 'forgotten';
  const confirmedOn =
    note.confirmed && note.confirmedAt !== null
      ? ` · confirmed by an owner on ${noteDate(note.confirmedAt)}`
      : '';
  const seen = note.origin === 'observed' && !forgotten ? ` · ${seenText(note.seenCount)}` : '';
  return (
    <li className="notebook-note" data-testid={`notebook-note-${note.id}`}>
      <div className="notebook-note-head">
        <span className="notebook-note-kind">{kindText(note.kind)}</span>
        {note.subject !== null ? (
          <code className="notebook-note-subject">{note.subject}</code>
        ) : (
          <span className="admin-muted">this part of the boat as a whole</span>
        )}
        <span className="notebook-note-badges">
          <StatusBadge status={status.badge} label={status.label} />
          {note.disputedAt !== null ? (
            <StatusBadge status="suggested" label="Disputed by the readings" />
          ) : null}
        </span>
      </div>
      {note.text !== null ? (
        <p className="notebook-note-text">{note.text}</p>
      ) : (
        <p className="notebook-note-text notebook-note-gone">Its words are gone.</p>
      )}
      <p className="notebook-note-meta">
        {sourceText(note)}
        {confirmedOn}
        {seen}
      </p>
      <p className="notebook-note-meta">
        First noted {noteDate(note.observedAt)}
        {forgotten
          ? ` · forgotten on ${noteDate(note.updatedAt)}`
          : ` · last checked ${noteDate(note.lastCheckedAt)}`}
      </p>
      {note.basis !== null ? <Basis title="What it rests on" basis={note.basis} /> : null}
      {note.disputedAt !== null && note.dispute !== null ? (
        <Basis
          title={`The readings disagreed on ${noteDate(note.disputedAt)}`}
          basis={note.dispute}
        />
      ) : null}
    </li>
  );
}

export function AdminBoatNotebook() {
  const { boatId } = useParams<{ boatId: string }>();
  const location = useLocation();
  // The linking page hands the boat's name over, so the page can name it before it opens.
  const passedName = (location.state as { boatName?: unknown } | null)?.boatName;
  const boatName = typeof passedName === 'string' ? passedName : null;
  const back = notebookBack(location.state);
  const [reason, setReason] = useState('');
  const [data, setData] = useState<NotebookView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function open(e: FormEvent) {
    e.preventDefault();
    if (boatId === undefined || reason.trim().length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi<NotebookView>(
        `/v1/admin/boats/${encodeURIComponent(boatId)}/notebook?reason=${encodeURIComponent(
          reason.trim()
        )}`
      );
      setData(res);
    } catch (err) {
      // A fixed line for each answer, never the server's message.
      const e2 = err as { status?: number; body?: { error?: unknown } };
      const code = typeof e2.body?.error === 'string' ? e2.body.error : undefined;
      setError(notebookErrorText(e2.status, code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="admin-section notebook-page">
      <p>
        <Link to={back.to}>← Back to {back.label}</Link>
      </p>

      {data === null ? (
        <>
          <header className="admin-section-header">
            <h1>{boatName !== null ? `${boatName}: notebook` : "A boat's notebook"}</h1>
            <GuideLink section="boat-notebook" />
          </header>
          <div className="admin-auth-banner">
            The notebook holds what this boat&apos;s owners told Neo about it, and what Neo noticed
            in its readings. Opening it is logged with your account and your reason.
          </div>
          <form className="neo-admin-reason" onSubmit={(e) => void open(e)}>
            <label htmlFor="notebook-reason">Reason for reading</label>
            <input
              id="notebook-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. checking a note Neo used in an answer"
              minLength={3}
              maxLength={200}
              required
            />
            <button
              type="submit"
              className="admin-button"
              disabled={loading || reason.trim().length < 3}
            >
              {loading ? 'Opening…' : 'Open notebook'}
            </button>
          </form>
          {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
        </>
      ) : (
        <>
          <header className="admin-section-header">
            <h1>{data.boat.displayName}: notebook</h1>
            <span className="admin-count" data-testid="notebook-counts">
              {countsText(data.notes)}
            </span>
            <GuideLink section="boat-notebook" />
          </header>
          <div className="admin-auth-banner">
            Your reading is logged with your reason. The notes never name an owner here: they say
            &ldquo;an owner&rdquo; or &ldquo;a former owner&rdquo;.
          </div>
          {data.boat.decommissioned ? (
            <p className="admin-hint">This boat is hidden from its owners (decommissioned).</p>
          ) : null}
          {data.notes.length === 0 ? (
            <p className="admin-empty">This boat has no notes yet.</p>
          ) : (
            notesByArea(data.notes).map((group) => (
              <section
                key={group.area}
                className="notebook-area"
                aria-labelledby={`notebook-area-${group.area}`}
                data-testid={`notebook-area-${group.area}`}
              >
                <h2 id={`notebook-area-${group.area}`}>
                  {group.title}{' '}
                  <span className="admin-count">
                    {group.notes.length} note{group.notes.length === 1 ? '' : 's'}
                  </span>
                </h2>
                <ul className="notebook-notes">
                  {group.notes.map((note) => (
                    <NoteCard key={note.id} note={note} />
                  ))}
                </ul>
              </section>
            ))
          )}
        </>
      )}
    </section>
  );
}
